import React, { useEffect, useState } from "react";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import CandidateShareDialog from "./CandidateShareDialog";
import { RECRUITING_NEXT_ACTIONS, publishRecruitingNextActions } from "../utils/recruitingNextActions";
import { loadInterviews, openInterviewScheduler, schedulingRequest } from "../utils/interviewScheduling";

const key = item => `${item.candidateId}:${item.jobId || ""}`;
export function CandidateRecruitingActions({ candidate }) {
  const assignments = candidate?.job_assignments?.length ? candidate.job_assignments : [
    { id: null, stage: candidate?.stage || candidate?.candidate_stage },
  ];
  const actions = assignments.filter(job => /^(presented|interview|interviewing)$/i.test(job.stage || ""));
  if (!actions.length) return null;
  return <Stack direction="row" spacing={1} sx={{ my: 1 }} flexWrap="wrap">
    {actions.map(job => <Button key={job.id || "profile"} onClick={() => publishRecruitingNextActions({
      recruiting_next_actions: [{ candidateId: candidate.candidate_id || candidate.id,
        candidateName: candidate.full_name || candidate.name || "Candidate", jobId: job.id,
        jobTitle: job.title,
        stage: job.stage, action: /^presented$/i.test(job.stage) ? "present" : "schedule" }],
    })}>Next action{job.title ? ` · ${job.title}` : ""}</Button>)}
  </Stack>;
}
export default function RecruitingNextActions() {
  const [queue, setQueue] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkedAction, setCheckedAction] = useState(null);
  const [scheduled, setScheduled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const current = queue[0];
  useEffect(() => {
    const receive = event => setQueue(previous => {
      const entries = new Map(previous.map(item => [key(item), item]));
      for (const item of event.detail || []) {
        if (item.candidateId && ["present", "schedule"].includes(item.action)) entries.set(key(item), item);
      }
      return [...entries.values()];
    });
    window.addEventListener(RECRUITING_NEXT_ACTIONS, receive);
    return () => window.removeEventListener(RECRUITING_NEXT_ACTIONS, receive);
  }, []);
  useEffect(() => {
    setError(""); setScheduled(false);
    if (!current || current.action !== "schedule") { setChecking(false); return; }
    let active = true;
    setChecking(true);
    loadInterviews({ candidateId: current.candidateId, jobId: current.jobId }).then(data => {
      if (active) setScheduled((data.interviews || []).some(record =>
        !["cancelled", "completed"].includes(record.interview_status) &&
        Date.parse(record.ends_at || record.starts_at) > Date.now()));
    }).catch(err => { if (active) setError(err.message); })
      .finally(() => { if (active) { setChecking(false); setCheckedAction(current); } });
    return () => { active = false; };
  }, [current]);
  const dismiss = () => { setSharing(false); setQueue(previous => previous.slice(1)); };
  const schedule = () => {
    openInterviewScheduler({ candidateId: current.candidateId, jobId: current.jobId,
      candidate: { id: current.candidateId, full_name: current.candidateName }, stage: "Interview" });
    dismiss();
  };
  const proceed = async () => {
    setBusy(true); setError("");
    try {
      await schedulingRequest(current.jobId ? `/jobs/${current.jobId}/matches/stage` : `/candidates/${current.candidateId}/stage`, {
        method: "PATCH", body: JSON.stringify({ stage: "Interview", ...(current.jobId ? { candidate_ids: [current.candidateId] } : {}) }),
      });
      setQueue(previous => previous.map(item => key(item) === key(current) ? { ...item, action: "schedule", stage: "Interview" } : item));
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  if (!current) return null;
  return <>
    <Dialog open={!sharing} onClose={busy ? undefined : dismiss} fullWidth maxWidth="sm">
      <DialogTitle data-popup-header>{current.action === "present" ? "Send Candidate profile" : "Schedule interview"}</DialogTitle>
      <DialogContent><Stack spacing={2}>
        <Typography fontWeight={600}>{current.candidateName}</Typography>
        {current.jobId && <Typography variant="body2">{[current.jobTitle || `Job ${current.jobId}`, current.companyName].filter(Boolean).join(" · ")}</Typography>}
        {queue.length > 1 && <Typography variant="caption">{queue.length} candidates need a next action</Typography>}
        {error && <Alert severity="error">{error}</Alert>}
        {current.action === "present" ? <>
          <Typography>The Candidate is now Presented. Review and share the profile with the Client / Hiring Manager.</Typography>
          <Button variant="contained" disabled={busy} onClick={() => setSharing(true)}>Review and send profile</Button>
          <Button disabled={busy} onClick={proceed}>Client / Hiring Manager wants to proceed</Button>
        </> : <>
          {checking || checkedAction !== current ? <CircularProgress size={24} /> : <>
            <Typography>{scheduled ? "An interview is already scheduled. Review the existing meeting if changes are needed." : "Choose a time and interviewers for the next interview."}</Typography>
            <Button variant="contained" onClick={schedule}>{scheduled ? "View scheduled interview" : "Schedule interview"}</Button>
          </>}
        </>}
      </Stack></DialogContent>
      <DialogActions data-popup-footer><Button disabled={busy} onClick={dismiss}>Later</Button></DialogActions>
    </Dialog>
    {sharing && <CandidateShareDialog open candidate={{ id: current.candidateId, full_name: current.candidateName }} onClose={() => setSharing(false)} />}
  </>;
}
